import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import TyreQuoteForm from "../components/TyreQuoteForm";

const PageHeader = styled.section`
  height: 300px;
  background: linear-gradient(rgba(220, 38, 38, 0.8), rgba(185, 28, 28, 0.8)), url("/images/land_hdr_bg.webp");
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  color: white;

  h1 {
    color: white;
    font-size: 3rem;
    text-align: center;
    width: 100%;
  }

  @media (max-width: 768px) {
    height: 200px;

    h1 {
      font-size: 2rem;
    }
  }
`;

const ContentSection = styled.section`
  padding: 80px 0;
  background: white;
`;

const TyreGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;

  .tyre-card {
    background: white;
    border-radius: 10px;
    padding: 2rem;
    text-align: center;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease;

    &:hover {
      transform: translateY(-10px);
    }

    .tyre-image {
      margin-bottom: 1.5rem;

      img {
        width: 100%;
        max-width: 200px;
        height: auto;
      }
    }

    .tyre-size {
      font-size: 1.1rem;
      font-weight: 600;
      color: #dc2626;

      span {
        color: #333;
      }
    }
  }
`;

const DescriptionSection = styled.div`
  margin: 3rem 0;
  padding: 2rem;
  background: #f8f9fa;
  border-radius: 10px;

  h2 {
    color: #dc2626;
    margin-bottom: 1.5rem;
    text-align: center;
  }

  p {
    color: #666;
    line-height: 1.8;
    margin-bottom: 1rem;
    text-align: center;
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin: 3rem 0;

  .feature-card {
    background: white;
    padding: 2rem;
    border-radius: 10px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    text-align: center;
    transition: transform 0.3s ease;

    &:hover {
      transform: translateY(-10px);
    }

    .feature-icon {
      width: 60px;
      height: 60px;
      background: #dc2626;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 1rem;
      color: white;
      font-size: 1.5rem;
      font-weight: bold;
    }

    h3 {
      color: #dc2626;
      margin-bottom: 1rem;
    }

    p {
      color: #666;
      line-height: 1.6;
    }
  }
`;

const LandluxTyres = () => {
  const tyreVariants = [
    {
      image: "/images/landlux_11r.webp",
      size: "11R22.5",
    },
    {
      image: "/images/landlux_295.webp",
      size: "295/80R22.5",
    },
    {
      image: "/images/landlux_275.webp",
      size: "275/70R22.5",
    },
  ];

  const features = [
    {
      icon: "💪",
      title: "Superior Durability",
      description: "Built to withstand the toughest road conditions with advanced rubber compounds and reinforced construction.",
    },
    {
      icon: "⚡",
      title: "Enhanced Performance",
      description: "Optimized tread patterns for superior traction, handling, and fuel efficiency in all weather conditions.",
    },
    {
      icon: "🛡️",
      title: "Safety First",
      description: "Advanced safety features including improved braking performance and resistance to punctures and cuts.",
    },
    {
      icon: "💰",
      title: "Cost Effective",
      description: "Excellent value for money with extended tyre life and reduced maintenance costs for fleet operators.",
    },
  ];

  return (
    <>
      <PageHeader>
        <div className="container">
          <motion.h1
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}>
            Landlux Tyres
          </motion.h1>
        </div>
      </PageHeader>

      <ContentSection>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}>
            <DescriptionSection>
              <h2>Premium Quality Tyres for Heavy-Duty Applications</h2>
              <p>
                Landlux tyres are engineered for the demanding Australian road conditions, offering exceptional performance for trucks, trailers, and
                heavy commercial vehicles. With advanced technology and superior materials, Landlux tyres deliver reliability, durability, and
                outstanding value.
              </p>
              <p>
                Our Landlux range features innovative tread designs, reinforced sidewalls, and premium rubber compounds that ensure maximum mileage
                and optimal performance across various terrains and weather conditions.
              </p>
            </DescriptionSection>

            <TyreGrid>
              {tyreVariants.map((tyre, index) => (
                <motion.div
                  key={index}
                  className="tyre-card"
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}>
                  <div className="tyre-image">
                    <img
                      src={tyre.image}
                      alt={`Landlux ${tyre.size}`}
                    />
                  </div>
                  <div className="tyre-size">
                    <span>Size:- </span>
                    {tyre.size}
                  </div>
                </motion.div>
              ))}
            </TyreGrid>

            <FeaturesGrid>
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className="feature-card"
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}>
                  <div className="feature-icon">{feature.icon}</div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </motion.div>
              ))}
            </FeaturesGrid>

            <TyreQuoteForm tyreType="Landlux" />
          </motion.div>
        </div>
      </ContentSection>
    </>
  );
};

export default LandluxTyres;
